import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { MatStepper } from "@angular/material/stepper";
import { Router } from "@angular/router";
import { User } from "@core/models";
import { LetItGoType } from "@core/models/letitgo-type";
import { TrainsInfo } from "@core/models/trainsInfo";
import {
  BookedService,
  NgxToastrService,
  TrainService,
  TravelUtilsService,
  UtilsService,
  AuthService,
  LanguageService,
} from "@core/services";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import { UserCardModalComponent } from "@shared/component/user-card-modal/user-card-modal.component";
import moment from "moment";
import { firstValueFrom, Subscription } from "rxjs";
import { take } from 'rxjs/operators';
import Swal from "sweetalert2";
import swal from "sweetalert2";
import { UserService } from '../../../../core/services/user.service';

@Component({
  selector: "app-confirm-booking",
  templateUrl: "./confirm-booking.component.html",
  styleUrls: ["./confirm-booking.component.scss"],
})
export class ConfirmBookingComponent implements OnInit, OnDestroy {
  @Input() user: User | undefined;
  @Input() selectedUsers: User[] | undefined;
  @Input() stepper: MatStepper | undefined;
  @Input() selectedTrains: any;
  totalPrice: number = 0


  @Input() isMocked: boolean | undefined;
  @Input() params: any;
  @Input() outwardSeats: any;
  @Input() returnSeats: any;
  @Input() isUser: any;
  @Input() isAdmin: boolean | undefined;
  @Input() userType: string | undefined;
  @Input() amount: any;
  @Input() farePriceMap: any;
  @Input() canRequest: boolean | undefined;
  @Input() trainsComplements: any;
  @Input() bookFailed: boolean | undefined;
  @Input() projectData: any;
  extraInfoForm: UntypedFormGroup | undefined;
  navigationExtras: any;
  userFilters: any = {};

  trainsList: any;
  userUuid: string | undefined;
  isEmployee: boolean = false;
  userToBook: string | undefined;
  canUseTRM: boolean = true;
  trainsInfo: TrainsInfo | null = {} as TrainsInfo;
  subscription: Subscription;
  priceChanged

  constructor(
    public trainService: TrainService,
    private travelUtilsService: TravelUtilsService,
    private utilsService: UtilsService,
    private translate: TranslateService,
    private router: Router,
    private modalService: NgbModal,
    private bookingService: BookedService,
    private authService: AuthService,
    private ngxToastrService: NgxToastrService,
    private fb: UntypedFormBuilder,
    private langService: LanguageService,
    private userService: UserService
  ) {
    this.subscription = this.trainService.getTrainsInfo$().subscribe((info: TrainsInfo | null) => {
      this.trainsInfo = info;
    });
    this.utilsService.socket.on("reserve-trains", (res) => {
      this.reserveTrainsResponse(res);
    });
  }

  ngOnDestroy(): void {
    this.utilsService.socket.off('reserve-trains');
    this.subscription.unsubscribe()
  }

  ngOnInit(): void {
    this.extraInfoForm = this.fb.group({
      customCode: [null],
      costCenter: [null],
      travellerReason: [null],
      annotationsExtras: [null],
    });
  }

  cancel() {
    history.back();
  }

  async showPaymentCardSelector(): Promise<any> {

    this.saveTrainRedirectState();

    const freshUser = await this.refreshTrainUser();

    if (!freshUser) {
      this.translate.get("company.card.error").subscribe((resp) => {
        this.ngxToastrService.typeInfo(null, resp);
      });
      return Promise.resolve(null);
    }

    if (!Array.isArray(freshUser.paymentCards)) {
      freshUser.paymentCards = freshUser.paymentCards ? [freshUser.paymentCards] : [];
    }

    if (!Array.isArray(freshUser.personalPaymentCards)) {
      freshUser.personalPaymentCards = freshUser.personalPaymentCards ? [freshUser.personalPaymentCards] : [];
    }

    freshUser.paymentCards = this.normalizeCardsForSelect(freshUser.paymentCards);
    freshUser.personalPaymentCards = this.normalizeCardsForSelect(freshUser.personalPaymentCards);

    if (
      this.trainsInfo?.company?.bookedWithoutCard
    ) {
      if (
        freshUser.personalPaymentCards.length === 0
      ) {
        return null;
      }

      const result = await Swal.fire({
        title: this.translate.instant("payment.pay-now-title"),
        text: this.translate.instant("payment.pay-now-question"),
        icon: "question",
        showCancelButton: true,
        confirmButtonText: this.translate.instant("payment.pay-now"),
        cancelButtonText: this.translate.instant("payment.pay-later"),
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: {
          popup: "flexible-payment-swal",
        },
      });

      if (!result.isConfirmed) {
        return null;
      }
    }

    const defaultToken =
      this.trainsInfo?.company?.cardRedsys?.find(c => c?.isDefault && c?.token)?.token || null;

    if (defaultToken) {
      freshUser.paymentCards = [
        ...freshUser.paymentCards.filter(c => c.token === defaultToken),
        ...freshUser.paymentCards.filter(c => c.token !== defaultToken),
      ];
    }

    this.trainService.setTrainsInfoPartial({ user: freshUser });

    return new Promise((resolve) => {
      const modalRef = this.modalService.open(UserCardModalComponent, {
        windowClass: 'custom-modal-user-card',
        centered: true,
        backdrop: 'static',
        keyboard: false
      });

      modalRef.componentInstance.bookCards = this.trainsInfo?.company?.bookedWithoutCard ? [] : freshUser.paymentCards;
      modalRef.componentInstance.bookPersonalCards = freshUser.personalPaymentCards;
      modalRef.componentInstance.fromBookSelect = true;
      modalRef.componentInstance.bookType = 'train';
      modalRef.componentInstance.user = freshUser;
      modalRef.componentInstance.company = this.trainsInfo?.company;

      modalRef.componentInstance.cardSelected.subscribe((selectedCard) => {
        resolve(selectedCard);
      });

      modalRef.result.catch(() => {
        resolve(null);
      });
    });
  }

  canConfirmBooking(): boolean {
    return !!this.trainsInfo?.userFilters;
  }

  isTrainAuthorizationRequired(): boolean {
    if (!this.canConfirmBooking()) {
      return false;
    }

    const isPrivilegedUser = !!this.trainsInfo?.isAdmin || !!this.trainsInfo?.isEmployee;

    return !isPrivilegedUser
      && (
        !!this.trainsInfo?.userFilters?.onlyTrainAuthorization
        || !this.isFareWithinRange()
      );
  }

  private buildTrainBookingRequest(correlation: string, selectedCard?: any, totalPrice?: any): any {
    const info = this.trainsInfo as TrainsInfo;
    const request: any = {
      language: this.langService.getLanguage(),
      userUuid: info?.userUuid,
      itineraryId: info?.itineraryId,
      booker: info?.travellersForm.value[0],
      passengers: info?.travellersForm.value,
      reservability: info?.reservability,
      token: info?.token,
      correlationId: correlation,
      customCode: this.extraInfoForm?.value.customCode,
      costCenter: this.extraInfoForm?.value.costCenter,
      annotationsExtras: this.extraInfoForm?.value.annotationsExtras,
      trainsInfo: {
        selectedExtras: info?.selectedExtras,
        selectedSeats: info?.selectedSeats,
        selectedTrains: info?.selectedTrains,
        searchConfig: info?.searchConfig,
        price: this.buildTrainPrice(totalPrice),
        itemizedPrice: info?.itemizedPrice
      }
    };

    if (selectedCard) {
      request.trainsInfo["paymentCard"] = {
        name: selectedCard.name,
        token: selectedCard.token
      };
    }

    return request;
  }

  private buildTrainPrice(totalPrice?: any): any {
    const info = this.trainsInfo as TrainsInfo;
    const price: any = {
      total: totalPrice ?? this.trainService.calculateFinalPrice(info, 'total'),
      outward: this.buildTrainDirectionPrice('outward')
    };

    if (info?.selectedTrains.selectedReturnAlternatives) {
      price['return'] = this.buildTrainDirectionPrice('return');
    }

    return price;
  }

  private buildTrainDirectionPrice(direction: 'outward' | 'return'): any {
    const info = this.trainsInfo as TrainsInfo;
    const selectedTrain = direction === 'outward'
      ? info?.selectedTrains.selectedOutwardTrain
      : info?.selectedTrains.selectedReturnTrain;
    const providerPrice = this.trainService.calculateProviderPrice(info, direction) * 1;
    const finalCommision = Number(selectedTrain?.finalCommision) || 0;

    return {
      finalPrice: providerPrice + finalCommision,
      iva: Number(selectedTrain?.iva) || 0,
      finalCommision,
      providerPrice,
      extras: this.getSelectedExtrasTotal(direction),
      seats: this.getSelectedSeatsTotal(direction),
    };
  }

  private getSelectedExtrasTotal(direction: 'outward' | 'return'): number {
    const extras = this.trainsInfo?.selectedExtras?.[direction];

    return Array.isArray(extras)
      ? extras.reduce((acc, extra) => acc + (Number(extra.price) * Number(extra.quantity) || 0), 0)
      : 0;
  }

  private getSelectedSeatsTotal(direction: 'outward' | 'return'): number {
    const seats = this.trainsInfo?.selectedSeats?.[direction];

    return Array.isArray(seats)
      ? this.trainService.getSeatsTotal(seats)
      : 0;
  }

  private validateSelectedTrainSections(): boolean {
    const selectedTrains = this.trainsInfo?.selectedTrains;

    if (!selectedTrains?.selectedOutwardTrain) {
      return true;
    }

    const outwardSectionIds = this.getTrainSectionIds(selectedTrains.selectedOutwardTrain);
    const hasInvalidOutwardSelection =
      this.hasItemsOutsideSections(this.trainsInfo?.selectedSeats?.outward, outwardSectionIds) ||
      this.hasItemsOutsideSections(this.trainsInfo?.selectedExtras?.outward, outwardSectionIds);

    const returnSectionIds = this.getTrainSectionIds(selectedTrains.selectedReturnTrain);
    const hasInvalidReturnSelection = !!selectedTrains.selectedReturnTrain && (
      this.hasItemsOutsideSections(this.trainsInfo?.selectedSeats?.return, returnSectionIds) ||
      this.hasItemsOutsideSections(this.trainsInfo?.selectedExtras?.return, returnSectionIds)
    );

    if (!hasInvalidOutwardSelection && !hasInvalidReturnSelection) {
      return true;
    }

    this.translate.get("train.messages.error").subscribe((result) => {
      this.ngxToastrService.typeInfo("Error", result);
    });

    return false;
  }

  private getTrainSectionIds(train: any): Set<string> {
    return new Set(
      (train?.sections || [])
        .map((section: any) => section?.sectionId || section?.id)
        .filter(Boolean)
        .map((sectionId: any) => `${sectionId}`)
    );
  }

  private hasItemsOutsideSections(items: any[] | undefined, sectionIds: Set<string>): boolean {
    return Array.isArray(items) && sectionIds.size > 0
      ? items.some((item) => item?.sectionId && !sectionIds.has(`${item.sectionId}`))
      : false;
  }

  onConfirm(): void {
    if (!this.canConfirmBooking()) {
      return;
    }

    if (this.isTrainAuthorizationRequired()) {
      this.requestAuthorization();
      return;
    }

    this.onReserve();
  }

  onReserve() {
    if (!this.canConfirmBooking()) {
      return;
    }

    if (!this.validateSelectedTrainSections()) {
      return;
    }

    if (this.isTrainAuthorizationRequired()) {
      this.requestAuthorization();
      return;
    }

    this.showPaymentCardSelector().then((selectedCard) => {
      if (!selectedCard && !this.trainsInfo?.company.bookedWithoutCard) {
        return;
      }
      this.utilsService.loadingTrains = true;
      const correlation = crypto.randomUUID();
      const request = this.buildTrainBookingRequest(correlation, selectedCard);
      this.trainService.addCorrelation(correlation);
      this.trainService.reserveTrains(request).subscribe(
        (s) => {
          console.log(s)
        },
        (err) => {
          console.log(err)
          this.letItGo()
          this.authService.totalAuthorizations++;
          this.redirectToInit()
        })
    })
  }

  requestAuthorization() {
    if (!this.validateSelectedTrainSections()) {
      return;
    }

    const correlation = crypto.randomUUID();
    const request = this.buildTrainBookingRequest(correlation);
    this.trainService.addCorrelation(correlation);

    const authorization = {
      type: "train",
      price: request.trainsInfo.price.total,
      requestData: request,
      fromDate: `${this.trainsInfo?.selectedTrains.selectedOutwardTrain.departDate}`,
      toDate:
        this.trainsInfo?.searchConfig?.returnDate != null
          ? `${this.trainsInfo.selectedTrains.selectedReturnTrain.departDate}`
          : "",
      customCode: this.extraInfoForm?.value.customCode,
      costCenter: this.extraInfoForm?.value.costCenter,
      travellerReason: this.extraInfoForm?.value.travellerReason,
      annotationsExtras: this.extraInfoForm?.value.annotationsExtras,
    };
    if (this.trainsInfo?.userUuid) {
      authorization["userUuid"] = this.trainsInfo?.userUuid;
    }
    this.utilsService.loadingTrains = true;
    this.bookingService.requestAuthorization(authorization).subscribe(
      () => {
        this.utilsService.loadingTrains = false;
        this.translate
          .get(["common.congrat", "authorization.train"])
          .subscribe((response) => {
            swal
              .fire({
                title: response["common.congrat"],
                text: response["authorization.train"],
                position: "center",
                showConfirmButton: false,
                icon: "success",
                timer: 5000,
              })
              .then(() => {
                this.authService.totalAuthorizations++;
                this.redirectToInit()
              });
            if (response.Amount != null && response.Amount.isNan()) {
              swal.close();
              this.utilsService.loadingTrains = false;
              this.translate.get("authorization.error").subscribe((resp) => {
                this.ngxToastrService.typeError(resp, "error");
              });
            }
          });
      },
      (err) => {
        if (err.status === 403 && err.error?.message === "This action is not allowed in this plan") {
          this.translate.get("authorization.not-allowed-plan").subscribe((translatedMessage) => {
            this.ngxToastrService.typeInfo('', translatedMessage);
          });
          // modalRef.close();
        }
        swal.close();
        this.utilsService.loadingTrains = false;
        this.letItGo()
        this.authService.totalAuthorizations++;
        this.redirectToInit()
      }
    )
  }

  letItGo() {
    type Alternative = {
      alternativeId: string;
      origin?: string;
      destination?: string;
      departDate?: string;
      arriveDate?: string;
      carrier?: string;
      fareName?: string;
      trainNumber?: string;
    };
    const selectedTrains = this.trainsInfo?.selectedTrains;
    const getSelectedSections = (
      sections: { sectionId: string; alternatives?: Alternative[] }[] = [],
      selectedAlternatives: string[] = []
    ) => sections.map((section) => ({
      ...section.alternatives?.find((alternative) => selectedAlternatives.includes(alternative.alternativeId)),
      sectionId: section.sectionId,
    }));
    const goingStops = getSelectedSections(selectedTrains?.selectedOutwardTrain?.sections, selectedTrains?.selectedOutwardAlternatives);
    const returningStops = getSelectedSections(selectedTrains?.selectedReturnTrain?.sections, selectedTrains?.selectedReturnAlternatives);
    const formatDate = (date?: string) => date && moment(date).isValid()
      ? moment(date).format("DD/MM/YYYY HH:mm") : "No disponible";
    const getItinerary = (section?: Alternative) => `${section?.origin || "No disponible"} - ${section?.destination || "No disponible"}`;
    const getStopsText = (sections: Alternative[], direction: string) => {
      const label = `${sections.length > 1 ? "Tramos" : "Tramo"} ${direction}:`;
      return `${label} ${sections.length ? sections.map((section) =>
        `${getItinerary(section)} (Carrier: ${section.carrier || "N/A"}, tarifa: ${section.fareName || "N/A"}, tren número: ${section.trainNumber || "N/A"}), sale de ${section.origin || "No disponible"} a las ${formatDate(section.departDate)}, llega a ${section.destination || "No disponible"} a las ${formatDate(section.arriveDate)}`
      ).join("; ") : "No disponible"}`;
    };
    const getFaresText = (sections: Alternative[]) => sections.length
      ? sections.map((section) => sections.length > 1
        ? `${getItinerary(section)}: ${section.fareName || "No disponible"}`
        : section.fareName || "No disponible").join("; ")
      : "No disponible";

    const body = {
      // quedan tarjetasde desciento
      travellers: this.trainsInfo?.travellers.map(
        (u) => `${u.name} ${u.lastname}, `
      ),
      itinerary: `${this.trainsInfo?.searchConfig?.origin?.name || "No disponible"} - ${this.trainsInfo?.searchConfig?.destination?.name || "No disponible"}`,
      dates: formatDate(goingStops[0]?.departDate || selectedTrains?.selectedOutwardTrain?.departDate),
      stopsText: getStopsText(goingStops, "ida"),
      fares: { outward: getFaresText(goingStops), return: "" },
      price: this.trainService.totalPrice(this.trainsInfo ?? {} as TrainsInfo),
      stops: goingStops.length,
      seats: {outward: (this.trainsInfo?.selectedSeats?.outward || []).map((s) =>
      ({
        carriage: s.carriage,
        position: s.position,
        itinerary: getItinerary(goingStops.find((section) => section.sectionId === s.sectionId))
      })), return: [] as {
          carriage: string;
          position: string;
          itinerary: string;
        }[]},
      extras: {outward: (this.trainsInfo?.selectedExtras?.outward || []).map((s) =>
      ({
        description: s.description,
        quantity: s.quantity,
        itinerary: getItinerary(goingStops.find((section) => section.sectionId === s.sectionId))
      })), return: [] as {
          description: string;
          quantity: string|number;
          itinerary: string;
        }[]},
      customCode: this.extraInfoForm?.value.customCode,
      costCenter: this.extraInfoForm?.value.costCenter,
      annotationsExtras: this.extraInfoForm?.value.annotationsExtras,
    };
    if (this.trainsInfo?.selectedTrains?.selectedReturnTrain) {
      const returnDate = formatDate(returningStops[0]?.departDate || selectedTrains?.selectedReturnTrain?.departDate)
      body.dates += " - " + `${returnDate}`;
      body.stops += returningStops.length
      body.fares.return = getFaresText(returningStops);
      this.trainsInfo?.selectedSeats?.return?.forEach((s) => {
        body.seats.return.push({
          carriage: s.carriage,
          position: s.position,
          itinerary: getItinerary(returningStops.find((section) => section.sectionId === s.sectionId))
        })
      })
      this.trainsInfo?.selectedExtras?.return?.forEach((s) => {
        body.extras.return.push({
          description: s.description,
          quantity: s.quantity,
          itinerary: getItinerary(returningStops.find((section) => section.sectionId === s.sectionId))
        })
      })
      body.stopsText += `\n${getStopsText(returningStops, "vuelta")}`;
    }
    this.travelUtilsService.removeTravelLocalStorage();

    if (this.trainsInfo?.searchConfig?.project && this.trainsInfo?.searchConfig?.project.uuid !== "none") {
      body["project"] = this.trainsInfo?.searchConfig?.project.title || "";
    }

    this.utilsService.sendEmailOperations(
      LetItGoType.train,
      body,
      this.trainsInfo?.user?.uuid
    );
    this.translate
      .get(["common.congrat", "common.operations"])
      .subscribe((response) => {
        swal
          .fire({
            position: "center",
            icon: "success",
            title: response["common.congrat"],
            text: response["common.operations"],
            showConfirmButton: false,
            timer: this.utilsService.letItGoTimer,
            timerProgressBar: true,
          })
          .then(() => {
            this.utilsService.loadingTrains = false;
          });
      });
  }

  reserveTrainsResponse(res) {
    if (this.trainService.existsCorrelation(res.correlationId)) {
      this.trainService.removeCorrelation(res.correlationId)
      this.utilsService.loadingTrains = false;
      if ((res.data.type && (res.data.type?.toLowerCase().includes("error") || res.data.type?.toLowerCase().includes("exception"))) || (res.data.bookingStatus && (res.data.bookingStatus?.toLowerCase().includes('unknown') || res.data.bookingStatus?.toLowerCase().includes('error')))) {
        if (res.data.status === 410 && res.data.message === "Sold Out") {
          this.translate
            .get([
              "train.error.sold-out",
            ])
            .subscribe((response) => {
              Swal.fire({
                position: "center",
                imageUrl:
                  "../../../../../assets/img/svg/check-circle.svg",
                title: '',
                text: response[
                  "train.error.sold-out"
                ],
                showConfirmButton: false,
                timer: 10000,
              })
            })
        } else if (res.data?.status === 818) {
          this.translate
            .get(["train.notAvailable"])
            .subscribe((result) => {
              this.ngxToastrService.typeInfo(
                "Error",
                result["train.notAvailable"]
              );
            })
        } else if (res?.data?.message?.toLowerCase().includes('invalid card') && this.trainsInfo?.searchConfig?.travellers.some((t: any) => t.selectedCard)) {
          this.translate
            .get(["train.invalid-card"])
            .subscribe((result) => {
              this.ngxToastrService.typeInfo(
                'Error',
                'train.invalid-card'
              );
            })
        } else if (res?.data?.status === 409 && res.data.message === "Price changed") {
          this.translate
            .get([
              "flight.detail-flight.price-changed",
              "flight.detail-flight.previous-price",
              "flight.detail-flight.current-price",
              "flight.detail-flight.change-detected",
              "project.accept",
              "common.cancel",
            ])
            .subscribe((translations) => {
              swal.fire({
                icon: "warning",
                title: translations["flight.detail-flight.change-detected"],
                html: `
                  <p>${translations["flight.detail-flight.price-changed"]}</p>
                  <p><strong>${translations["flight.detail-flight.previous-price"]}:</strong> ${this.trainService.calculateFinalPrice(this.trainsInfo as TrainsInfo)} €</p>
                  <p><strong>${translations["flight.detail-flight.current-price"]}:</strong> ${res.data.content.finalPrice} €</p>
                `,
                showCancelButton: true,
                confirmButtonText: translations["project.accept"],
                cancelButtonText: translations["common.cancel"],
                customClass: {
                  popup: "price-change-popup",
                  confirmButton: "simple-confirm-btn",
                  cancelButton: "simple-cancel-btn",
                },
                buttonsStyling: false,
              }).then((result) => {
                if (result.isConfirmed) {
                  this.priceChanged = res.data.content.finalPrice;
                  this.showPaymentCardSelector().then((selectedCard) => {
                    if (!selectedCard && !this.trainsInfo?.company.bookedWithoutCard) {
                      return;
                    }
                    this.utilsService.loadingTrains = true;
                    const correlation = res.correlationId;
                    const request = this.buildTrainBookingRequest(correlation, selectedCard, res.data.content.finalPrice);
                    this.trainService.addCorrelation(correlation);
                    this.trainService.createReservation(request).subscribe(
                      (s) => {
                        console.log(s)
                      },
                      (err) => {
                        console.log(err)
                        this.letItGo()
                        this.authService.totalAuthorizations++;
                        this.redirectToInit()
                      }
                    )
                  })
                } else {
                  this.utilsService.loadingTrains = false;
                }
              });
            });
        } else {
          this.subscription = this.trainService.getTrainsInfo$().subscribe((info: TrainsInfo | null) => {
            if (!info) return;
            this.trainsInfo = info;
            this.letItGo()
            if (this.trainsInfo?.searchConfig?.project && this.trainsInfo?.searchConfig.project.uuid !== "none") {
              res.forEach((booking: any) => {
                this.addToTravel(booking);
              });
            }
            this.redirectToInit()
          });
        }
      } else {
        console.log(res)
        this.translate
          .get(["common.congrat", "train.success"])
          .subscribe((response) => {
            this.utilsService.loadingTrains = false;
            swal
              .fire({
                position: "center",
                icon: "success",
                title: response["common.congrat"],
                text: response["train.success"],
                showConfirmButton: false,
                timer: 3000,
              })
              .then(() => {
                this.subscription = this.trainService.getTrainsInfo$().subscribe((info: TrainsInfo | null) => {
                  if (!info) return;
                  this.trainsInfo = info;
                  if (this.trainsInfo?.searchConfig?.project && this.trainsInfo?.searchConfig.project.uuid !== "none") {
                    res.forEach((booking: any) => {
                      this.addToTravel(booking);
                    });
                  }
                  this.redirectToInit()
                });
              });
          });
      }
    }
  }

  redirectToInit() {
    this.utilsService.loadingTrains = false;
    
    const route = this.getNav()
    if (route) {
      this.router.navigate([route], { queryParams: { specialAction: true } });
    }
    this.trainService.clearTrainsInfo();
  }

  getNav() {
    const companyUuid = this.trainsInfo.company ? this.trainsInfo?.company?.uuid : ""
    const roleToRouteMap = {
      EMPLOYEE: companyUuid ? "/companies/" + companyUuid : "/companies/",
      AGENCY: companyUuid ? "/companies/" + companyUuid : "/companies/",
      HUB: companyUuid ? "/companies/" + companyUuid : "/companies/",
      COMPANY: "/my-company/details",
      USER: "/home"
    };
  
    const userType = this.trainsInfo.user?.type?.toUpperCase();
    const accessType = this.trainsInfo.user?.accessType?.toUpperCase();


    if (!this.trainsInfo.isEmployee && this.trainsInfo.canUseTRM === false) {
      return "/home";
    }

    if (this.trainsInfo.isEmployee) {
      return roleToRouteMap["EMPLOYEE"]
    }

    return roleToRouteMap[userType] || roleToRouteMap[accessType];
  }

  addToTravel(train: any) {
    if (this.utilsService.hasProcessedUuid(train.uuid)) {
      return;
    }
    this.utilsService.addProcessedUuid(train.uuid);

    const checkInAux = moment(train.departDate).format("YYYY-MM-DD");
    const addBody: any = {
      item: {
        type: "train",
        uuid: train.uuid,
        initDate: checkInAux,
      },
    };
    if (train.returnDate != null) {
      const checkOutAux = moment(train.returnDate).format("YYYY-MM-DD");
      addBody.item.endDate = checkOutAux;
    }
    this.travelUtilsService.addItemToTravel(addBody, this.projectData.selectedProject.uuid);
  }

  cleanData() {
    this.trainsInfo = {} as TrainsInfo;;
  }

  isFareWithinRange(): boolean {
    if (
      !this.trainsInfo?.userFilters ||
      this.trainsInfo?.userFilters?.priceMinTrain == null ||
      this.trainsInfo?.userFilters?.priceMaxTrain == null
    ) {
      return true;
    }

    const totalPrice = Number(this.trainService.calculateFinalPrice(this.trainsInfo as TrainsInfo));

    return (
      totalPrice >= this.trainsInfo?.userFilters.priceMinTrain &&
      totalPrice <= this.trainsInfo?.userFilters.priceMaxTrain
    );
  }

  private normalizeCardsForSelect(cards: any[] = []): any[] {
    const cardsWithName = (cards || []).filter(card =>
      card?.token &&
      typeof card?.name === 'string' &&
      card.name.trim() !== ''
    );

    return cardsWithName.reduce((acc, currentCard) => {
      const currentKey = `${currentCard.token}-${currentCard.name.trim()}`;
      const exists = acc.some(card => `${card.token}-${card.name.trim()}` === currentKey);

      if (!exists) {
        acc.push(currentCard);
      }

      return acc;
    }, []);
  }

  private async refreshTrainUser(): Promise<any> {
    const userUuid = this.trainsInfo?.userToBook || this.trainsInfo?.userUuid || this.trainsInfo?.user?.uuid;

    if (!userUuid) {
      return this.trainsInfo?.user;
    }

    const freshUser = await firstValueFrom(
      this.userService.getUser(userUuid).pipe(take(1))
    );

    this.trainService.setTrainsInfoPartial({ user: freshUser });
    this.trainsInfo.user = freshUser;

    return freshUser;
  }

  private saveTrainRedirectState(): void {
    if (!this.trainsInfo) {
      return;
    }

    const stateToSave: any = {
      ...this.trainsInfo,

      travellersFormValue: this.trainsInfo.travellersForm?.value || null,
      formSearchTrainValue: this.trainsInfo.formSearchTrain?.value || null,

      extraInfoFormValue: this.extraInfoForm?.value || null,
      createdAt: Date.now(),
    };

    delete stateToSave.travellersForm;
    delete stateToSave.formSearchTrain;

    localStorage.setItem('stateTrainRedirect', JSON.stringify(stateToSave));
  }

}
